import { collection, doc } from 'firebase/firestore';

/** Map file extensions to Monaco language ids. */
const EXTENSION_LANGUAGE_MAP = {
  js: 'javascript',
  jsx: 'javascript',
  mjs: 'javascript',
  cjs: 'javascript',
  ts: 'typescript',
  tsx: 'typescript',
  json: 'json',
  css: 'css',
  scss: 'scss',
  html: 'html',
  htm: 'html',
  md: 'markdown',
  yml: 'yaml',
  yaml: 'yaml',
  py: 'python',
  sh: 'shell',
  sql: 'sql',
  xml: 'xml',
  go: 'go',
  rs: 'rust',
  java: 'java',
};

/**
 * @param {unknown} value
 * @returns {string}
 */
export function normalizePatchContent(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch (err) {
    void err;
    return String(value);
  }
}

/** Statuses shown in the human pending queue (post- or mid-automated QA). */
export const PENDING_SANDBOX_STATUSES = [
  'awaiting_judge_request',
  'pending_review',
  'queued_for_qa',
  'awaiting_human_qa',
];

/** Processed sandbox document statuses shown in History. */
export const HISTORY_SANDBOX_STATUSES = [
  'approved',
  'rejected',
  'discarded',
  'failed',
  'success',
  'needs_revision',
];

/**
 * Truncate a Firestore sandbox id for compact UI (keep ends for uniqueness).
 * @param {string|null|undefined} sandboxId
 * @returns {string}
 */
export function formatSandboxIdShort(sandboxId) {
  const id = typeof sandboxId === 'string' ? sandboxId.trim() : '';
  if (!id) return '';
  if (id.length <= 14) return id;
  return `${id.slice(0, 8)}…${id.slice(-4)}`;
}

/**
 * @param {string|null|undefined} sandboxId
 * @param {string|null|undefined} query
 * @returns {boolean}
 */
export function sandboxIdMatchesQuery(sandboxId, query) {
  const id = typeof sandboxId === 'string' ? sandboxId.trim().toLowerCase() : '';
  const q = typeof query === 'string' ? query.trim().toLowerCase() : '';
  if (!id || !q) return false;
  return id === q || id.startsWith(q) || id.includes(q);
}

/**
 * @param {unknown} raw
 * @returns {object | null}
 */
export function normalizeCodeSandboxPatch(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = typeof raw.id === 'string' ? raw.id.trim() : '';
  if (!id) return null;

  return {
    ...raw,
    id,
    target_file_path:
      typeof raw.target_file_path === 'string' ? raw.target_file_path.trim() : '',
    patch_content: normalizePatchContent(raw.patch_content),
    rationale: typeof raw.rationale === 'string' ? raw.rationale.trim() : '',
    action_type:
      typeof raw.action_type === 'string' && raw.action_type.trim() ?
        raw.action_type.trim().toLowerCase() :
        'update',
    agentId: typeof raw.agentId === 'string' ? raw.agentId.trim() : null,
    ticketId: typeof raw.ticketId === 'string' ? raw.ticketId.trim() : null,
    status: typeof raw.status === 'string' ? raw.status : 'pending_review',
    deployMode:
      typeof raw.deployMode === 'string' ? raw.deployMode.trim() : '',
    deployTargets: Array.isArray(raw.deployTargets) ? raw.deployTargets : [],
    fleetTemplateId:
      typeof raw.fleetTemplateId === 'string' ? raw.fleetTemplateId.trim() : '',
    githubDeployStatus:
      typeof raw.githubDeployStatus === 'string' ? raw.githubDeployStatus : '',
    githubDeployError:
      typeof raw.githubDeployError === 'string' ? raw.githubDeployError.trim() : '',
    githubDeploySummary:
      raw.githubDeploySummary && typeof raw.githubDeploySummary === 'object'
        ? raw.githubDeploySummary
        : null,
    pullRequestUrl:
      typeof raw.pullRequestUrl === 'string' ? raw.pullRequestUrl.trim() : '',
    reviewNote: typeof raw.reviewNote === 'string' ? raw.reviewNote.trim() : '',
  };
}

/**
 * @param {object | null | undefined} patch
 * @returns {boolean}
 */
export function isFleetSandboxPatch(patch) {
  return (
    patch?.deployMode === 'fleet' ||
    (Array.isArray(patch?.deployTargets) && patch.deployTargets.length > 0)
  );
}

/**
 * @param {object | null | undefined} patch
 * @returns {{ total: number, succeeded: number, failed: number, pending: number }}
 */
export function fleetDeploySummaryCounts(patch) {
  const s = patch?.githubDeploySummary;
  if (s && typeof s === 'object') {
    return {
      total: Number(s.total) || 0,
      succeeded: Number(s.succeeded) || 0,
      failed: Number(s.failed) || 0,
      pending: Number(s.pending) || 0,
    };
  }
  const targets = Array.isArray(patch?.deployTargets) ? patch.deployTargets.length : 0;
  return { total: targets, succeeded: 0, failed: 0, pending: targets };
}

/**
 * @param {object | null | undefined} patch
 * @returns {string}
 */
export function codeSandboxDisplayStatus(patch) {
  const status = patch?.status || '';
  const deployStatus = patch?.githubDeployStatus || '';
  const fleet = isFleetSandboxPatch(patch);

  if (status === 'rejected') return 'rejected';
  if (status === 'discarded') return 'discarded';

  if (fleet && deployStatus === 'deploying') return 'fleet_deploying';
  if (fleet && deployStatus === 'partial') return 'fleet_partial';
  if (fleet && (deployStatus === 'failed' || status === 'failed')) {
    return 'fleet_failed';
  }
  if (fleet && (deployStatus === 'success' || status === 'success')) {
    return 'fleet_success';
  }

  if (deployStatus === 'failed' || status === 'failed') return 'failed';
  if (deployStatus === 'success' || status === 'success') return 'success';
  if (status === 'approved') return 'approved';
  if (status === 'awaiting_human_qa') return 'awaiting_human_qa';
  if (status === 'queued_for_qa') return 'queued_for_qa';
  if (status === 'pending_review') return 'pending_review';
  if (status === 'awaiting_judge_request') return 'awaiting_judge_request';
  return status || 'unknown';
}

/**
 * @param {object | null | undefined} patch
 * @returns {string}
 */
export function codeSandboxStatusLabel(patch) {
  const displayStatus = codeSandboxDisplayStatus(patch);
  const counts = fleetDeploySummaryCounts(patch);

  if (displayStatus === 'awaiting_judge_request') {
    return 'Awaiting Automated QA request';
  }
  if (displayStatus === 'pending_review') return 'Automated QA';
  if (displayStatus === 'queued_for_qa') return 'Queued for QA';
  if (displayStatus === 'awaiting_human_qa') {
    if (patch?.judgeSkippedReason === 'ticket_awaiting_human_intervention') {
      return 'Skipped — ticket awaiting human review';
    }
    if (patch?.judgeSkippedReason === 'path_judge_reject_cap') {
      return 'Human QA — path Judge reject cap';
    }
    return 'Awaiting human QA';
  }
  if (displayStatus === 'needs_revision') return 'Needs revision';
  if (displayStatus === 'approved') return 'Approved';
  if (displayStatus === 'rejected') return 'Rejected';
  if (displayStatus === 'discarded') return 'Discarded';

  if (displayStatus === 'fleet_deploying') {
    return `Deploying · ${counts.succeeded}/${counts.total || '?'}`;
  }
  if (displayStatus === 'fleet_partial') {
    return `Partial · ${counts.failed} failed`;
  }
  if (displayStatus === 'fleet_success') {
    return `Deployed · ${counts.succeeded}/${counts.total} PRs`;
  }
  if (displayStatus === 'fleet_failed') {
    return `Deploy failed · ${counts.succeeded}/${counts.total || 0}`;
  }

  if (displayStatus === 'failed') return 'Deploy failed';
  if (displayStatus === 'success') return 'Deployed';
  return displayStatus;
}

/**
 * @param {object | null | undefined} patch
 * @returns {string}
 */
export function sandboxDeployErrorMessage(patch) {
  if (patch?.githubDeployError) return patch.githubDeployError;
  if (codeSandboxDisplayStatus(patch) === 'failed') {
    return 'GitHub deployment failed.';
  }
  return '';
}

/**
 * @param {object | null | undefined} patch
 * @returns {boolean}
 */
export function sandboxHasDeployError(patch) {
  return Boolean(sandboxDeployErrorMessage(patch));
}

/**
 * @param {string | null | undefined} filePath
 * @returns {string}
 */
export function monacoLanguageFromPath(filePath) {
  try {
    if (!filePath || typeof filePath !== 'string') return 'javascript';
    const base = filePath.split('/').pop() || '';
    const dot = base.lastIndexOf('.');
    if (dot < 0) return 'plaintext';
    const ext = base.slice(dot + 1).toLowerCase();
    return EXTENSION_LANGUAGE_MAP[ext] || 'plaintext';
  } catch (err) {
    console.error('monacoLanguageFromPath:', err);
    return 'javascript';
  }
}

/**
 * @param {string | null | undefined} actionType
 * @returns {string}
 */
export function codeSandboxActionLabel(actionType) {
  const value = String(actionType || 'update').toLowerCase();
  if (value === 'create') return 'Create file';
  if (value === 'delete') return 'Delete file';
  return 'Update file';
}

/**
 * @param {import('firebase/firestore').Firestore} db
 * @param {string} companyId
 * @returns {import('firebase/firestore').CollectionReference}
 */
export function codeSandboxesCollection(db, companyId) {
  return collection(db, 'companies', companyId, 'code_sandboxes');
}

/**
 * @param {import('firebase/firestore').Firestore} db
 * @param {string} companyId
 * @param {string} sandboxId
 * @returns {import('firebase/firestore').DocumentReference}
 */
export function codeSandboxDocRef(db, companyId, sandboxId) {
  return doc(db, 'companies', companyId, 'code_sandboxes', sandboxId);
}
